import React, { useMemo } from "react";

import { Button, Col, Container, Row, Stack } from "react-bootstrap";

import classNames from "classnames";
import currency from "currency.js";
import { Link } from "react-router-dom";
import { ReactSVG } from "react-svg";

import type { ResaleInput } from "@api/graphql";
import SVGPen from "@assets/icons/streamline/Content-Pen-Write--Streamline-Ultimate.svg";
import type { FieldFormatter } from "@components/detail_drawer/FieldFormatter";
import { useNextResaleIndex } from "@states/resale_submission";
import {
  ArtistAliveDisplay,
  capitalizeFirstLetter,
  formatDateStringLong,
  getDisplayValueForAcquiredDateChoices,
  prefix0ToSingleDigitNumber,
} from "@util/index";

import { ROUTES } from "../../routes";
import type { FormStepProps } from "../form/MultiStepForm";

import { DetailCell } from "./DetailCell";

import styles from "./ReviewResale.module.scss";

interface ReviewResaleProps extends FormStepProps<ResaleInput, ResaleInput> {}

const FIELD_FORMATTERS: FieldFormatter<ResaleInput>[] = [
  { title: "Artist", format: (multiFormData) => multiFormData.artist?.provisional_artist_name },
  {
    title: "Medium",
    format: (multiFormData) =>
      capitalizeFirstLetter(
        multiFormData.artwork?.medium.toLocaleLowerCase().replace(/_/g, " ").replace(/ book/, "' book") ?? "",
      ),
  },
  {
    title: "Sold date",
    format: (multiFormData) => formatDateStringLong(multiFormData.resale?.artwork_sale_date),
  },
  {
    title: "Resale price",
    format: (multiFormData) => currency(multiFormData.resale?.artwork_sale_price ?? 0).format(),
  },
  {
    title: "Acquired after 8th June 2010",
    format: (multiFormData) => getDisplayValueForAcquiredDateChoices(multiFormData.resale?.acquired_date_choice),
  },
  {
    title: "Country",
    format: (multiFormData) => multiFormData.artist?.provisional_artist_country,
  },
  {
    title: "Is the artist living",
    format: (multiFormData) =>
      typeof multiFormData.artist?.provisional_is_artist_alive === "boolean"
        ? multiFormData.artist?.provisional_is_artist_alive === true
          ? ArtistAliveDisplay.YES
          : ArtistAliveDisplay.NO
        : ArtistAliveDisplay.UNKNOWN,
  },
  {
    title: "Year of death",
    format: (multiFormData) => multiFormData.artist?.provisional_artist_yod,
  },
];

export const ReviewResale = ({ multiFormData, onSubmit }: ReviewResaleProps) => {
  const nextResaleIndex = useNextResaleIndex(multiFormData);

  // We don't want the index to be changed right after the report is added to the recoil state
  // So use empty dependency, which should construct currentResaleIndex only when user lands to the page
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const nextResaleIndexFormatted = useMemo(() => prefix0ToSingleDigitNumber(nextResaleIndex), []);

  const handleSubmit = () => onSubmit(multiFormData);

  return (
    <Container fluid>
      <Stack gap={5} className="pb-5">
        <Stack>
          <Row>
            <Col className={styles.title}>
              <span className={classNames(styles.index, "h5")}>Report {nextResaleIndexFormatted}</span>
              <span className={classNames(styles.banner, "h3")}>Review report</span>
            </Col>
          </Row>
        </Stack>
        <Stack className={styles.body} gap={4}>
          <Row className={styles.header}>
            <Col className={styles.header_title}>Artwork detail</Col>
            <Col xs={{ span: "auto", offset: 1 }}>
              <Link to={ROUTES.ResaleSubmission.ArtistDetails.path}>
                <Button variant="none">
                  <ReactSVG src={SVGPen} />
                  <span className="b3 bold">Edit</span>
                </Button>
              </Link>
            </Col>
          </Row>
          <Row>
            <span className={styles.artwork_name}>{multiFormData?.artwork?.artwork_name}</span>
          </Row>
          <Row>
            {FIELD_FORMATTERS.map(({ title, format }) => (
              <Col xs={6} key={title} className={styles.detail}>
                <DetailCell title={title} value={format(multiFormData)} />
              </Col>
            ))}
          </Row>
        </Stack>
        <Stack className={styles.button_container}>
          <Col>
            <Button onClick={handleSubmit}>Confirm details</Button>
          </Col>
        </Stack>
      </Stack>
    </Container>
  );
};
